import type {
  AgencyObligationOverlayHistoryView,
  AgencyObligationOverlayWorkspaceResponse,
  AgencyObligationOverlayWriteResult,
  CreateAgencyObligationOverlayBody,
  UpdateAgencyObligationOverlayBody,
} from '@safetyhub/shared';
import { api, type ApiResponse } from './api';

/**
 * 기관 웹 **서식 변형(오버레이)** API — 서버 `AgencyObligationOverlaysController`(`legal-documents/agency-overlays`).
 * 계약 정본은 shared `legal-obligation-overlays.ts`.
 * ⚠️ `lib/legalDocuments.ts`에 두지 않은 이유: 그 모듈을 `vi.mock`하는 기존 spec들이 팩토리에 없는 export를 import하면
 *    통째로 죽는다(CLAUDE.md 「vi.mock된 모듈에 새 export」) — 그래서 파일을 갈랐다.
 */

export const legalObligationOverlayQueryKeys = {
  all: ['legal-obligation-overlays'] as const,
  workspace: (companyId: string) => ['legal-obligation-overlays', companyId, 'workspace'] as const,
  history: (companyId: string, overlayId: string) =>
    ['legal-obligation-overlays', companyId, 'history', overlayId] as const,
};

const BASE = '/legal-documents/agency-overlays';

function unwrap<T>(body: ApiResponse<T>): T {
  if (!body.success || body.data === null) throw body;
  return body.data;
}

export async function getAgencyOverlayWorkspace(
  companyId: string
): Promise<AgencyObligationOverlayWorkspaceResponse> {
  const { data } = await api.get<ApiResponse<AgencyObligationOverlayWorkspaceResponse>>(BASE, {
    params: { companyId },
  });
  return unwrap(data);
}

export async function createAgencyOverlay(
  companyId: string,
  body: CreateAgencyObligationOverlayBody
): Promise<AgencyObligationOverlayWriteResult> {
  const { data } = await api.post<ApiResponse<AgencyObligationOverlayWriteResult>>(BASE, body, {
    params: { companyId },
  });
  return unwrap(data);
}

export async function updateAgencyOverlay(
  companyId: string,
  overlayId: string,
  body: UpdateAgencyObligationOverlayBody
): Promise<AgencyObligationOverlayWriteResult> {
  const { data } = await api.patch<ApiResponse<AgencyObligationOverlayWriteResult>>(
    `${BASE}/${encodeURIComponent(overlayId)}`,
    body,
    { params: { companyId } }
  );
  return unwrap(data);
}

export async function getAgencyOverlayHistory(
  companyId: string,
  overlayId: string
): Promise<AgencyObligationOverlayHistoryView[]> {
  const { data } = await api.get<ApiResponse<AgencyObligationOverlayHistoryView[]>>(
    `${BASE}/${encodeURIComponent(overlayId)}/history`,
    { params: { companyId } }
  );
  return unwrap(data);
}
